class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
  }

  get predictiveSearchResults() {
    return this.querySelector('[data-predictive-search]');
  }

  get popularContent() {
    return this.querySelector('[data-popular-content]');
  }

  get allPredictiveSearchInstances() {
    return document.querySelectorAll('predictive-search');
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.predictiveListenersAttached) {
      this.predictiveListenersAttached = true;
      this.addEventListener('focusin', (event) => {
        if (event.target.matches('input[type="search"]')) {
          this.onFocus();
        }
      });
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));

      this.addEventListener('click', (event) => {
        const tabBtn = event.target.closest('.predictive-search__tab-btn');
        if (tabBtn) {
          event.preventDefault();
          const tabName = tabBtn.dataset.tab;
          this.querySelectorAll('.predictive-search__tab-btn').forEach((btn) => btn.classList.remove('active'));
          this.querySelectorAll('.predictive-search__tab-panel').forEach((panel) => panel.classList.remove('active'));
          tabBtn.classList.add('active');
          this.querySelector(`.predictive-search__tab-panel[data-tab-panel="${tabName}"]`)?.classList.add('active');
        }
      });

      setTimeout(() => {
        if (this.input?.form) {
          this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
        }
      }, 0);
    }
  }

  setupEventListeners() {
    // Handled in connectedCallback
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    const popularContent = this.popularContent;
    const predictiveSearchResults = this.predictiveSearchResults;

    if (newSearchTerm.length > 0) {
      if (popularContent) {
        popularContent.classList.add('hidden');
        popularContent.style.display = 'none';
      }
      if (predictiveSearchResults) {
        predictiveSearchResults.style.display = 'block';
      }
    } else {
      if (popularContent) {
        popularContent.classList.remove('hidden');
        popularContent.style.display = 'block';
      }
      if (predictiveSearchResults) {
        predictiveSearchResults.style.display = 'none';
      }
    }

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      if (this.popularContent) {
        this.popularContent.classList.remove('hidden');
        this.popularContent.style.display = 'block';
      }
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.style.display = 'none';
      }
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    const popularContent = this.popularContent;
    const predictiveSearchResults = this.predictiveSearchResults;

    if (!currentSearchTerm.length) {
      if (popularContent) {
        popularContent.classList.remove('hidden');
        popularContent.style.display = 'block';
      }
      if (predictiveSearchResults) {
        predictiveSearchResults.style.display = 'none';
      }
      return;
    }

    if (popularContent) {
      popularContent.classList.add('hidden');
      popularContent.style.display = 'none';
    }
    if (predictiveSearchResults) {
      predictiveSearchResults.style.display = 'block';
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (this.closest('.search-drawer')) return;
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    // Reset abort controller for new request
    if (this.abortController) {
      try {
        this.abortController.abort();
      } catch (e) {}
    }
    this.abortController = new AbortController();

    const searchUrl =
      (window.routes && window.routes.predictive_search_url) ||
      (typeof routes !== 'undefined' && routes.predictive_search_url) ||
      '/search/suggest';

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,collection,query,page,article',
      'resources[limit]': '10',
      'resources[options][limit]': '10',
      'resources[limit_scope]': 'each',
      'resources[options][limit_scope]': 'each',
      'resources[options][fields]': 'author,product_type,title,variants.sku,vendor',
      section_id: 'predictive-search',
    });

    fetch(`${searchUrl}?${params.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const parsedDoc = new DOMParser().parseFromString(text, 'text/html');
        const sectionEl =
          parsedDoc.querySelector('#shopify-section-predictive-search') ||
          parsedDoc.querySelector('[id*="predictive-search"]') ||
          parsedDoc.querySelector('#predictive-search-results') ||
          parsedDoc.body;
        const resultsMarkup = sectionEl ? sectionEl.innerHTML : text;

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') {
          // Request was aborted cleanly
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults?.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!statusText || !this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement?.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (this.popularContent) {
      this.popularContent.classList.add('hidden');
      this.popularContent.style.display = 'none';
    }
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = 'block';
      this.predictiveSearchResults.innerHTML = resultsMarkup;
    }
    this.setAttribute('results', true);

    // Show/hide sticky View All button in drawer footer
    const footer = this.querySelector('[data-search-footer]');
    if (footer) {
      const wrapper = this.predictiveSearchResults?.querySelector('[data-show-view-all]');
      if (wrapper && wrapper.getAttribute('data-show-view-all') === 'true') {
        footer.style.display = 'block';
      } else {
        footer.style.display = 'none';
      }
    }

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) {
      this.setLiveRegionText(countEl.textContent);
    }
  }

  getResultsMaxHeight() {
    if (this.closest('.search-drawer')) {
      return 'none';
    }
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (this.closest('.search-drawer')) {
      this.predictiveSearchResults.style.maxHeight = 'none';
    } else {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    if (!this.closest('.search-drawer') || clearSearchTerm || !this.input.value.trim().length) {
      this.predictiveSearchResults.removeAttribute('style');
    }

    // Hide footer when closed or cleared
    const footer = this.querySelector('[data-search-footer]');
    if (footer) {
      footer.style.display = 'none';
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
